"use client";

import * as React from "react";
import { ChevronLeftIcon } from "lucide-react";

import { cn } from "../../lib/utils";

/** How the back control reads: the round chevron of a pushed screen, or a plain text word ("Annuler") on a form. */
export enum MobileScreenHeaderBackStyle {
  Chevron = "chevron",
  Text = "text",
}

export type MobileScreenHeaderBack = {
  label: string;
  onBack: () => void;
  style?: MobileScreenHeaderBackStyle;
};

type MobileScreenHeaderContextValue = {
  /**
   * The back button a screen gets when it does not pass its own. The app provides it on every stack screen (the
   * shared back rule) and leaves it null on the tab screens, which have nothing to go back to.
   */
  defaultBack: MobileScreenHeaderBack | null;
};

const MobileScreenHeaderContext = React.createContext<MobileScreenHeaderContextValue>({ defaultBack: null });

/**
 * Set by the app shell around the page content (and reset to no back inside full-screen sheets, which draw their
 * own). Lives here so the DataTable's title row and the app's own screens read the same default.
 */
export const MobileScreenHeaderProvider = MobileScreenHeaderContext.Provider;

export function useMobileScreenHeaderDefaultBack(): MobileScreenHeaderBack | null {
  return React.useContext(MobileScreenHeaderContext).defaultBack;
}

/**
 * The pinned look, exported for a host whose sticky box has to be an ANCESTOR of the header (the DataTable pins its
 * whole toolbar, because a sticky element only sticks within its parent and the toolbar's parent is the list).
 * Canvas-coloured and bled to the page gutters so rows scrolling under it never show through at the edges.
 */
export const mobileScreenHeaderStickyClassName =
  "sticky top-0 z-30 -mx-4 bg-[var(--app-canvas)] px-4 pt-4 pb-5 sm:-mx-6 sm:px-6";

type MobileScreenHeaderProps = {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  /**
   * Omitted: the screen's default back from {@link MobileScreenHeaderProvider} (none on a tab screen). `null`: no
   * back button even on a stack screen.
   */
  back?: MobileScreenHeaderBack | null;
  /**
   * At most TWO round buttons: the screen's one primary action and a ⋯ holding everything else. The DataTable
   * applies this rule itself; a screen passing its own actions must keep to it.
   */
  actions?: React.ReactNode;
  /** Pinned under the title row with it: a search field, chips, segmented tabs. */
  children?: React.ReactNode;
  /** False when the host already pins it (a full-screen sheet's bar, the DataTable's sticky toolbar). */
  sticky?: boolean;
  className?: string;
  /** Rendered as the title element, so a sheet can pass its accessible title component. */
  titleAs?: React.ElementType;
};

/**
 * The phone screen header: one bar for every screen that is not a tab. A round back button, the title, and up to
 * two round actions beside it, with optional search or chips pinned underneath.
 *
 * The title is NEVER cut with an ellipsis. It stays on one line, stepping down from 26px to 20px to fit beside
 * the buttons ("Income Statement" next to back and two actions); only a title too long even at 20px wraps,
 * balanced, with the buttons aligned to its first line. Keeping actions to two is what makes room for it: four
 * round buttons beside a 28px title left about ten characters, which is how "Transacti…" happened.
 *
 * Pinned by default: it stays at the top of the page's scroll area while only the content under it moves.
 */
export function MobileScreenHeader({
  title,
  subtitle,
  back,
  actions,
  children,
  sticky = true,
  className,
  titleAs: TitleTag = "h1",
}: MobileScreenHeaderProps) {
  const defaultBack = useMobileScreenHeaderDefaultBack();
  const resolvedBack = back === undefined ? defaultBack : back;
  const titleRef = React.useRef<HTMLElement>(null);
  useFitTitleToOneLine(titleRef);

  return (
    <div
      // A pinned header carries the page's top padding itself (the app shell drops its own `pt-4` when it finds
      // this marker), so the header sits at the same height before and after it sticks, on every screen.
      data-page-sticky-header={sticky ? "" : undefined}
      className={cn("flex min-w-0 flex-col gap-3", sticky && mobileScreenHeaderStickyClassName, className)}
    >
      <div className='flex min-w-0 items-start gap-2'>
        {resolvedBack ? <MobileScreenHeaderBackButton back={resolvedBack} /> : null}
        <div className='flex min-h-11 min-w-0 flex-1 flex-col justify-center'>
          <TitleTag
            ref={titleRef}
            className='text-[1.625rem] leading-tight font-extrabold tracking-tight text-balance break-words text-foreground'
          >
            {title}
          </TitleTag>
          {subtitle ? <p className='text-[13px] font-semibold break-words text-muted-foreground'>{subtitle}</p> : null}
        </div>
        {/* Every header button is the same round shape, whatever each action renders. */}
        {actions ? (
          <div className='flex shrink-0 items-center gap-2 [&_button]:!rounded-full [&>*>button]:size-11'>{actions}</div>
        ) : null}
      </div>
      {children}
    </div>
  );
}

const TITLE_MAX_PX = 26;
const TITLE_MIN_PX = 20;

/**
 * Sizes the title to the largest step between {@link TITLE_MAX_PX} and {@link TITLE_MIN_PX} that keeps it on one
 * line, measured on one unwrapped line at the full size. Below the minimum it stays at the minimum and wraps.
 * Re-fits when the header's width or the title's text changes.
 */
function useFitTitleToOneLine(ref: React.RefObject<HTMLElement | null>) {
  React.useLayoutEffect(() => {
    const element = ref.current;
    if (!element || typeof ResizeObserver === "undefined") return;
    const fit = () => {
      element.style.fontSize = `${TITLE_MAX_PX}px`;
      element.style.whiteSpace = "nowrap";
      const available = element.clientWidth;
      const needed = element.scrollWidth;
      element.style.whiteSpace = "";
      if (!available || needed <= available) {
        element.style.fontSize = "";
        return;
      }
      element.style.fontSize = `${Math.max(TITLE_MIN_PX, Math.floor((TITLE_MAX_PX * available) / needed))}px`;
    };
    fit();
    const resize = new ResizeObserver(fit);
    resize.observe(element.parentElement ?? element);
    const text = new MutationObserver(fit);
    text.observe(element, { characterData: true, childList: true, subtree: true });
    return () => {
      resize.disconnect();
      text.disconnect();
    };
  }, [ref]);
}

function MobileScreenHeaderBackButton({ back }: { back: MobileScreenHeaderBack }) {
  if (back.style === MobileScreenHeaderBackStyle.Text) {
    return (
      <button
        type='button'
        onClick={back.onBack}
        className='inline-flex h-11 shrink-0 items-center rounded-full px-1 text-base font-bold text-primary active:bg-primary/10'
      >
        {back.label}
      </button>
    );
  }
  return (
    <button
      type='button'
      onClick={back.onBack}
      aria-label={back.label}
      className='inline-flex size-11 shrink-0 items-center justify-center rounded-full bg-card text-foreground shadow-sm ring-1 ring-border/70 active:bg-muted'
    >
      <ChevronLeftIcon aria-hidden className='size-5' />
    </button>
  );
}
